import { useState } from "react";

import { CommunicationsUIStateInbox } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { PortalCommunicationsClient } from "@/Communications/Api/Dto/PortalCommunicationsClient";
import { defaultPortalCommunicationFilterSettingQuerySize } from "@/Communications/Models/PortalCommunication/PortalCommunicationFilterSettings";
import { usePortalCommunicationFilterSetting } from "./usePortalCommunicationFilterSetting";

interface IUsePortalCommunicationViewPagination {
    totalSharedItems: number;
    absoluteIndex: number;
    isPreviousButtonDisabled: boolean;

    onPreviousPortalCommunication: () => Promise<void>;
    onNextPortalCommunication: (isCommunicationRemoved: boolean) => Promise<void>;
}

function usePortalCommunicationViewPagination(communicationsUIStateInbox: CommunicationsUIStateInbox, onReturnToInbox: () => void): IUsePortalCommunicationViewPagination {
    const { currentSharedItemId, portalCommunicationTotalCount } = communicationsStore;
    const currentPortalCommunicationTableSetting = communicationsStore.portalCommunicationTableSetting[communicationsUIStateInbox];

    const sharedItemsIds = communicationsStore.fetchedSharedItemIds ?? [];
    const currentInboxPageNumber = currentPortalCommunicationTableSetting?.currentPageNumber ?? 1;
    const [loading, setLoading] = useState(false);

    const isSentInbox = communicationsUIStateInbox === CommunicationsUIStateInbox.Sent;
    const { currentFilterSetting: filterSetting } = usePortalCommunicationFilterSetting(communicationsUIStateInbox);

    const getSharedItemIdByIndex = (index: number): string => sharedItemsIds[index];

    const totalSharedItems = portalCommunicationTotalCount ?? 0;
    const currentIndex = sharedItemsIds.findIndex(p => p === currentSharedItemId) ?? 0;
    const queryPageSize = filterSetting.queryPageSize ?? defaultPortalCommunicationFilterSettingQuerySize;
    const absoluteIndex = currentIndex + queryPageSize * (currentInboxPageNumber - 1) + 1;
    const isLastIndex: boolean = sharedItemsIds.length - 1 === currentIndex;
    const isPreviousButtonDisabled = currentInboxPageNumber === 1 && currentIndex === 0;

    const handleNavigation = (sharedItemId: string): void => {
        sessionStore.dispatch(LocalOperations.Communications.SetCurrentSharedItemId, {
            currentSharedItemId: sharedItemId
        });
    };

    const handleUpdateSharedItemsAndCurrentPage = async (direction: "next" | "previous"): Promise<void> => {
        const currentPage = direction === "next" ?
            currentInboxPageNumber + 1 :
            currentInboxPageNumber - 1;

        try {
            const updatedSharedItemIds = await updateSharedItemIds(currentPage);

            if (updatedSharedItemIds.length > 0) {
                const targetIndex = direction === "next" ? 0 : updatedSharedItemIds.length - 1;
                handleNavigation(updatedSharedItemIds[targetIndex]);
            }
            else {
                onReturnToInbox();
            }
        }
        finally {
            updateCurrentPageNumber(currentPage);
        }
    };

    const onPreviousPortalCommunication = async (): Promise<void> => {
        if (loading) return;
        setLoading(true);

        if (currentInboxPageNumber > 1 && currentIndex === 0) {
            await handleUpdateSharedItemsAndCurrentPage("previous");
        }
        else {
            handleNavigation(getSharedItemIdByIndex(currentIndex - 1));
        }
        setLoading(false);
    };

    const onNextPortalCommunication = async (isCommunicationRemoved: boolean): Promise<void> => {
        if (loading) return;
        setLoading(true);

        const inboxPageCount = Math.ceil(totalSharedItems / queryPageSize);
        if (currentInboxPageNumber < inboxPageCount && isLastIndex) {
            await handleUpdateSharedItemsAndCurrentPage("next");
        }
        else if (isLastIndex) {
            if (isCommunicationRemoved && currentInboxPageNumber > 1)
                updateCurrentPageNumber(currentInboxPageNumber - 1);
            onReturnToInbox();
        }
        else {
            handleNavigation(getSharedItemIdByIndex(currentIndex + 1));
        }
        setLoading(false);
    };

    const updateSharedItemIds = async (currentPageNumber: number): Promise<string[]> => {
        const settingKey = isSentInbox ? "SentFilterSetting" : "InboxFilterSetting";
        const response = await PortalCommunicationsClient.fetchPortalCommunications({
            InboxType: communicationsUIStateInbox,
            [settingKey]: filterSetting,
            CurrentPageNumber: currentPageNumber,
            QueryOrderDirection: currentPortalCommunicationTableSetting?.queryOrderDirection,
            QueryOrderType: currentPortalCommunicationTableSetting?.queryOrderType,
            SearchText: currentPortalCommunicationTableSetting?.searchText
        });

        if (response) {
            const updatedSharedItemsIds = response.portalCommunicationSharedItemIds;

            sessionStore.dispatch(LocalOperations.Communications.UpdatePortalCommunicationFetchedSharedItemsAndTotalCount, {
                sharedItemIds: updatedSharedItemsIds,
                totalCount: response.totalCount
            });
            return updatedSharedItemsIds;
        }
        else {
            return [];
        }
    };

    const updateCurrentPageNumber = (currentPageNumber: number): void => {
        const updatedTableSettings = { ...currentPortalCommunicationTableSetting, currentPageNumber: currentPageNumber };
        sessionStore.dispatch(
            LocalOperations.Communications.SavePortalCommunicationTableSetting,
            {
                tableSetting: updatedTableSettings,
                inboxType: communicationsUIStateInbox
            }
        );
    };

    return {
        totalSharedItems,
        absoluteIndex,
        isPreviousButtonDisabled,
        onPreviousPortalCommunication,
        onNextPortalCommunication
    };
}

export { usePortalCommunicationViewPagination };
